<template>
	<div class="row-actions-dropdown" ref="dropdownWrapper" :class="{ 'is-open': isOpen }">
		<button
			type="button"
			class="row-actions-dropdown__trigger"
			:class="{ active: isOpen }"
			:aria-label="$t('actions')"
			@click.stop="toggleDropdown"
			ref="trigger"
		>
			<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
				<path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
			</svg>
		</button>

		<div v-if="isOpen" ref="portal" class="row-actions-dropdown__portal">
			<div class="row-actions-dropdown__backdrop" @click.stop="closeDropdown" />
			<div
				ref="menu"
				class="row-actions-dropdown__menu"
				:style="menuStyle"
				@click.stop
			>
					<div class="row-actions-dropdown__section">
						<div class="row-actions-dropdown__title">{{ $t('device_actions') }}</div>
						<div class="row-actions-dropdown__actions">
							<div class="row-actions-dropdown__action" :class="{ active: actionFlags.is_update }">
								<RestartUpdateBtn
									:isUpdateBtn="true"
									:flag="{ name: 'is_update', isOn: actionFlags.is_update }"
									@toggleFlag="onToggleFlag"
								/>
								<span>{{ $t('update') }}</span>
							</div>
							<div class="row-actions-dropdown__action" :class="{ active: actionFlags.is_restart }">
								<RestartUpdateBtn
									:isRestartBtn="true"
									:flag="{ name: 'is_restart', isOn: actionFlags.is_restart }"
									@toggleFlag="onToggleFlag"
								/>
								<span>{{ $t('restart') }}</span>
							</div>
						</div>
					</div>

					<div v-if="Object.keys(actionFlags.user_flags).length > 0" class="row-actions-dropdown__section">
						<div class="row-actions-dropdown__title">{{ $t('device_settings') }}</div>
						<div class="row-actions-dropdown__toggles">
							<div
								v-for="(flagValue, flagName, idx) in actionFlags.user_flags"
								:key="flagName + idx"
								class="row-actions-dropdown__toggle-item"
							>
								<Tooltip :texts="flagTooltipTexts(flagName)">
									<div class="row-actions-dropdown__toggle-content">
										<span class="row-actions-dropdown__toggle-label">{{ $t(flagName.split('is_')[1]) }}</span>
										<IOSSwitchButton
											@toggleFlag="onToggleFlag"
											:userFlag="{ name: flagName, isOn: flagValue }"
											:sensorFlag="{ name: flagName, isOn: actionFlags.sensor_flags[flagName] }"
											:isDisabled="isFlagDisabled(flagName)"
										/>
									</div>
								</Tooltip>
							</div>
						</div>
					</div>
			</div>
		</div>
	</div>
</template>

<script>
import IOSSwitchButton from '@/modules/common/components/IOSSwitchButton'
import RestartUpdateBtn from '@/modules/common/icons/RestartUpdateBtn'
import Tooltip from '@/modules/common/components/Tooltip'
import { getFlagTooltipTexts, isFlagLocked } from '../utils/deviceFlagUtils'

const MENU_WIDTH = 248
const VIEWPORT_PADDING = 12
// Minimum space below the trigger to keep the menu opening downward (it scrolls internally)
const MIN_SPACE_BELOW = 280

export default {
	name: 'DeviceRowActionsDropdown',
	components: { IOSSwitchButton, RestartUpdateBtn, Tooltip },
	props: {
		actionFlags: {
			type: Object,
			required: true,
		},
	},
	data() {
		return {
			isOpen: false,
			menuStyle: {},
		}
	},
	mounted() {
		document.addEventListener('click', this.handleDocumentClick)
		document.addEventListener('keydown', this.handleKeydown)
		window.addEventListener('resize', this.handleViewportChange)
		window.addEventListener('scroll', this.handleViewportChange, true)
	},
	beforeDestroy() {
		this.removePortalFromBody()
		document.removeEventListener('click', this.handleDocumentClick)
		document.removeEventListener('keydown', this.handleKeydown)
		window.removeEventListener('resize', this.handleViewportChange)
		window.removeEventListener('scroll', this.handleViewportChange, true)
	},
	methods: {
		toggleDropdown() {
			if (this.isOpen) {
				this.closeDropdown()
				return
			}
			this.isOpen = true
			this.$nextTick(() => {
				// Move the menu to <body> so position:fixed is relative to the real
				// viewport (immune to ancestor transform/overflow contexts)
				if (this.$refs.portal) document.body.appendChild(this.$refs.portal)
				this.updateMenuPosition()
			})
		},
		closeDropdown() {
			this.removePortalFromBody()
			this.isOpen = false
			this.menuStyle = {}
		},
		removePortalFromBody() {
			const portal = this.$refs.portal
			if (portal && portal.parentNode === document.body) {
				document.body.removeChild(portal)
			}
		},
		handleDocumentClick(event) {
			if (!this.isOpen) return
			const insideTrigger = this.$refs.dropdownWrapper && this.$refs.dropdownWrapper.contains(event.target)
			const insideMenu = this.$refs.portal && this.$refs.portal.contains(event.target)
			// Clicks on a SweetAlert confirmation must not close the dropdown:
			// closing destroys the toggle mid-confirmation and its emit is lost
			const insideSwal = event.target.closest && event.target.closest('.swal2-container')
			if (!insideTrigger && !insideMenu && !insideSwal) {
				this.closeDropdown()
			}
		},
		handleKeydown(event) {
			if (this.isOpen && event.key === 'Escape') this.closeDropdown()
		},
		handleViewportChange() {
			if (!this.isOpen) return
			this.updateMenuPosition()
		},
		updateMenuPosition() {
			const trigger = this.$refs.trigger
			const menu = this.$refs.menu
			if (!trigger || !menu) return

			// On mobile the menu is centered via CSS - no inline positioning needed
			if (window.innerWidth <= 820) {
				this.menuStyle = { visibility: 'visible' }
				return
			}

			const triggerRect = trigger.getBoundingClientRect()
			const menuHeight = menu.scrollHeight
			const gap = 8
			const isRTL = window.getComputedStyle(this.$el).direction === 'rtl'

			const spaceBelow = window.innerHeight - triggerRect.bottom - gap - VIEWPORT_PADDING
			const spaceAbove = triggerRect.top - gap - VIEWPORT_PADDING

			// Keep the menu anchored to the clicked row: prefer opening downward
			// (scrolling internally if needed); only open upward when the space
			// below is really small and there is more room above.
			let top
			let maxHeight
			const openBelow = menuHeight <= spaceBelow || spaceBelow >= MIN_SPACE_BELOW || spaceBelow >= spaceAbove
			if (openBelow) {
				maxHeight = Math.min(menuHeight, Math.max(spaceBelow, 120))
				top = triggerRect.bottom + gap
			} else {
				maxHeight = Math.min(menuHeight, Math.max(spaceAbove, 120))
				top = triggerRect.top - gap - maxHeight
			}

			// Anchor the menu edge to the trigger edge, extending inward over the table
			let left = isRTL ? triggerRect.left : triggerRect.right - MENU_WIDTH
			left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - MENU_WIDTH - VIEWPORT_PADDING))

			this.menuStyle = {
				top: `${top}px`,
				left: `${left}px`,
				width: `${MENU_WIDTH}px`,
				maxHeight: `${maxHeight}px`,
				visibility: 'visible',
			}
		},
		onToggleFlag(flag) {
			this.$emit('toggleFlag', flag)
		},
		flagTooltipTexts(flagName) {
			return getFlagTooltipTexts(flagName, (key) => this.$t(key), this.actionFlags.user_flags)
		},
		isFlagDisabled(flagName) {
			return isFlagLocked(flagName, this.actionFlags.user_flags)
		},
	},
}
</script>

<style lang="scss" scoped>
@use '@/styles/vars.scss' as *;
@use '@/styles/mixins' as *;

.row-actions-dropdown {
	position: relative;
	display: inline-flex;
	align-items: center;
	justify-content: center;

	&.is-open {
		z-index: 1;
	}

	&__trigger {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 32px;
		height: 32px;
		padding: 0;
		border: 1px solid $grey-border-color;
		border-radius: 8px;
		background: $white;
		color: $dark-grey;
		cursor: pointer;
		transition: background 0.2s, border-color 0.2s, color 0.2s, box-shadow 0.2s;

		&:hover,
		&.active {
			background: $light-blue;
			border-color: $blue;
			color: $blue;
			box-shadow: 0 0 0 3px rgba(56, 156, 235, 0.12);
		}

		@media (max-width: 820px) {
			width: 36px;
			height: 36px;
		}
	}

	&__backdrop {
		position: fixed;
		inset: 0;
		z-index: 9997;
		background: rgba(15, 23, 42, 0.08);

		@media (max-width: 820px) {
			background: rgba(15, 23, 42, 0.35);
		}
	}

	&__menu {
		position: fixed;
		z-index: 9998;
		// Hidden until positioned by JS (prevents a one-frame flash at the wrong spot)
		visibility: hidden;
		background: $white;
		border: 1px solid $grey-border-color;
		border-radius: 10px;
		box-shadow: 0 12px 32px rgba(15, 23, 42, 0.16);
		overflow: hidden;
		max-height: calc(100vh - 24px);
		overflow-y: auto;

		// Mobile: centered modal-style menu
		@media (max-width: 820px) {
			top: 50% !important;
			left: 50% !important;
			transform: translate(-50%, -50%);
			width: min(320px, calc(100vw - 32px)) !important;
			max-height: 80vh;
			border-radius: 12px;
			box-shadow: 0 16px 48px rgba(15, 23, 42, 0.3);
		}
	}

	&__section {
		padding: 10px 0;

		&:not(:last-child) {
			border-bottom: 1px solid $grey-border-color;
		}

		@media (max-width: 820px) {
			padding: 12px 0;
		}
	}

	&__title {
		padding: 0 14px 8px;
		font-size: $font-size-tiny;
		font-weight: $font-weight-bold;
		color: $dark-grey;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	&__actions,
	&__toggles {
		display: flex;
		flex-direction: column;
		gap: 2px;
		padding: 0 8px 4px;
	}

	&__action,
	&__toggle-content {
		display: flex;
		align-items: center;
		gap: 10px;
		width: 100%;
		padding: 10px 12px;
		border-radius: 6px;
		transition: background 0.2s;

		@media (max-width: 820px) {
			padding: 12px 14px;
		}
	}

	&__action {
		font-size: $font-size-small;
		color: $black-blue;

		&:hover,
		&.active {
			background: $light-grey;
		}

		span {
			flex: 1;
			white-space: nowrap;
		}

		::v-deep .icon-container button {
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0;
			border: none;
			background: transparent;
			cursor: pointer;
		}
	}

	&__toggle-content {
		justify-content: space-between;
		gap: 16px;

		&:hover {
			background: $light-grey;
		}

		::v-deep .ios-switch.md-switch {
			margin: 0;
			flex-shrink: 0;
		}
	}

	&__toggle-label {
		flex: 1;
		min-width: 0;
		font-size: $font-size-small;
		color: $black-blue;
		text-transform: capitalize;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}

.row-actions-dropdown__backdrop,
.row-actions-dropdown__menu {
	animation: row-actions-fade-in 0.15s ease;
}

@keyframes row-actions-fade-in {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}
</style>
